import { PanelRightClose, Plus, X } from "lucide-react";
import { Suspense } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { INSPECTOR_RENDERERS, type PaneRenderer } from "@/components/workspace/inspectorRegistry";
import { NewTabSelector } from "@/components/workspace/NewTabSelector";
import { useWorkspaceShell } from "@/components/workspace/WorkspaceShell";
import { useInspector } from "@/contexts/InspectorContext";

/**
 * The inspector body: a tab strip plus the active tab's pane. Rendering is
 * dispatched through `INSPECTOR_RENDERERS` keyed by `kind` — there is no inline
 * switch and no pane internals are imported here. Only the active tab's pane is
 * mounted (matching the old single-DocPanel behavior), keeping heavy viewers
 * lazy.
 *
 * When no tab is active (no tabs open, or user clicked "+"), the NewTabSelector
 * is shown so users can open any supported tab kind.
 */
export function InspectorPane() {
  const { tabs, activeTabId, setActiveTab, closeTab, showSelector } = useInspector();
  const { toggleInspector } = useWorkspaceShell();
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;

  const hasTabs = tabs.length > 0;

  function renderActiveTab(tab: NonNullable<typeof activeTab>) {
    const renderer = INSPECTOR_RENDERERS[tab.kind] as unknown as PaneRenderer<unknown>;
    const PaneComponent = renderer.Component;
    return <PaneComponent payload={tab.payload} active />;
  }

  return (
    <div id="inspector-panel" className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex min-h-12 items-center gap-1 border-b bg-card pr-2">
        {hasTabs ? (
          <Tabs value={activeTabId ?? ""} onValueChange={setActiveTab} className="min-w-0 flex-1">
            <TabsList className="h-auto justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-1">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id} className="group max-w-48 gap-1.5 data-[state=active]:bg-muted">
                  <span className="truncate">{tab.title}</span>
                  {!tab.pinned ? (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Close ${tab.title}`}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        closeTab(tab.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          closeTab(tab.id);
                        }
                      }}
                      className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    >
                      <X className="size-3" />
                    </span>
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : (
          <div className="min-w-0 flex-1" />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="New tab"
          onClick={showSelector}
        >
          <Plus className="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:text-foreground md:hidden"
          aria-label="Collapse inspector"
          onClick={toggleInspector}
        >
          <PanelRightClose className="size-4" />
        </Button>
      </div>
      <div className="min-h-0 min-w-0 flex-1">
        {activeTab ? (
          <Suspense
            fallback={
              <div className="space-y-3 p-4">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-64 w-full" />
              </div>
            }
          >
            {renderActiveTab(activeTab)}
          </Suspense>
        ) : (
          <NewTabSelector />
        )}
      </div>
    </div>
  );
}
